import { useCallback } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { EntityBadgeById } from '@/lib/crm/components/badges/EntityBadgeById';
import { useOpenSlideout } from '@/lib/crm/hooks/useOpenSlideout';
import type { Research } from '@/lib/crm/record/research/useResearch';
import { getObject } from '@/lib/crm/store/crmObjectStore';

import type { MeetingPrepCompany } from '../utils/meetingPrepTypes';

const PROPERTY_LABELS: Record<string, string> = {
  founded: 'Founded',
  headcount: 'Headcount',
  industry: 'Industry',
  location: 'Location',
};

interface MeetingPrepCompanyCardProps {
  company: MeetingPrepCompany;
  research?: Research;
}

function ResearchLoadingSkeleton() {
  return (
    <div className='animate-pulse mt-2'>
      <div className='h-3 bg-[var(--color-bg-tertiary)] rounded w-full mb-1' />
      <div className='h-3 bg-[var(--color-bg-tertiary)] rounded w-3/4' />
    </div>
  );
}

export function MeetingPrepCompanyCard({
  company,
  research,
}: MeetingPrepCompanyCardProps) {
  const openSlideout = useOpenSlideout();

  // getObject is an imperative store read (no subscription); call on every
  // render so cached store updates are reflected without memoizing stale data.
  const companyObject = company.entity_id
    ? getObject(company.entity_id)
    : undefined;

  const handleViewResearch = useCallback(() => {
    if (company.research_entity_id) {
      openSlideout(company.research_entity_id);
    } else {
      openSlideout(company.entity_id);
    }
  }, [openSlideout, company.entity_id, company.research_entity_id]);

  const researchStatus = research?.status ?? null;

  const validProperties = Object.entries(PROPERTY_LABELS).filter(([key]) => {
    const value = company.properties[key];
    return value !== null && value !== undefined && value !== '';
  });

  const showFactGrid = validProperties.length > 0;

  const showResearchLoading =
    (!!company.research_entity_id && research === undefined) ||
    researchStatus === 'queued' ||
    researchStatus === 'in_progress';

  const showResearchBlock =
    !showResearchLoading &&
    research !== undefined &&
    researchStatus !== 'failed' &&
    researchStatus === 'complete';

  return (
    <div className='space-y-2 py-3 border-b border-[var(--color-border-secondary)] last:border-b-0'>
      {/* Header */}
      <div>
        {companyObject ? (
          <EntityBadgeById
            entityId={companyObject.entity_id}
            orgId={companyObject.org_id}
            variant='xs'
          />
        ) : (
          <span className='text-[13px] font-semibold text-[var(--color-text-primary)]'>
            {company.name ?? company.entity_id}
          </span>
        )}
        {company.description && (
          <p className='text-xs text-[var(--color-text-secondary)] mt-1'>
            {company.description}
          </p>
        )}
      </div>

      {/* Fact grid */}
      {showFactGrid && (
        <dl className='grid grid-cols-2 gap-x-4 gap-y-2 mt-3'>
          {validProperties.map(([key, displayLabel]) => (
            <div key={key}>
              <dt className='text-xs text-[var(--color-text-tertiary)]'>
                {displayLabel}
              </dt>
              <dd className='text-xs font-medium text-[var(--color-text-primary)]'>
                {company.properties[key]}
              </dd>
            </div>
          ))}
        </dl>
      )}

      {/* Research block */}
      {showResearchLoading && <ResearchLoadingSkeleton />}

      {showResearchBlock && research && (
        <div className='rounded-lg bg-[var(--color-bg-secondary)] p-3 mt-2'>
          {research.summary ? (
            <p className='text-xs text-[var(--color-text-secondary)] line-clamp-3'>
              {research.summary}
            </p>
          ) : (
            <p className='text-xs text-[var(--color-text-tertiary)]'>
              Research complete
            </p>
          )}
          <Button
            color='link-color'
            size='sm'
            onClick={handleViewResearch}
            className='mt-1'
          >
            View full research
          </Button>
        </div>
      )}
    </div>
  );
}
